import { useCallback, useRef } from 'react';
import { Message } from 'ai';
import {
  createVoiceTranscriptFolder,
  type VoiceFoldMessage,
} from '@/lib/chat-interface/voice-transcript-fold';

/**
 * Mirrors a voice session's turns into the chat UI.
 *
 * Display only, by design. A voice transcript must never be sent to /api/chat:
 * the voice agent already owns the conversation and its tools, so re-sending the
 * transcript runs a second, independent agent that answers in text nobody hears
 * and can still call tools. That second agent once submitted a smart form using
 * an email address the caller was in the middle of correcting.
 *
 * Deduplication of interim transcripts lives in `voice-transcript-fold`.
 */
export function useChatVoice(
  setMessages: (updater: (prev: Message[]) => Message[]) => void
) {
  const folderRef = useRef<ReturnType<typeof createVoiceTranscriptFolder> | null>(null);
  if (folderRef.current === null) {
    folderRef.current = createVoiceTranscriptFolder();
  }

  const handleTranscriptReceived = useCallback(
    (transcript: string, isFinal: boolean, segmentId?: string) => {
      if (!isFinal) return;
      setMessages(
        (prev) =>
          folderRef.current!.user(
            prev as unknown as VoiceFoldMessage[],
            transcript,
            segmentId
          ) as unknown as Message[]
      );
    },
    [setMessages]
  );

  const handleAgentResponseReceived = useCallback(
    (response: string, segmentId?: string) => {
      setMessages(
        (prev) =>
          folderRef.current!.agent(
            prev as unknown as VoiceFoldMessage[],
            response,
            segmentId
          ) as unknown as Message[]
      );
    },
    [setMessages]
  );

  return {
    handleTranscriptReceived,
    handleAgentResponseReceived,
  };
}
